import React, { useReducer } from "react";
import {
  Button,
  Flex,
  Box,
  useDisclosure,
  useToast,
  ModalBody,
} from "@chakra-ui/react";
import { useMutation } from "@apollo/client";
import { CREATE_CHILD, RESET_PASSWORD } from "../../graphql/mutations";
import { DataTable } from "../DataTable";
import { ModalBox } from "../ModalBox";
import { StudentsInviteBox } from "../StudentsInviteBox";
import { initialState } from "../../reducers/ProfilePage/initialState";
import { reducer } from "../../reducers/ProfilePage/reducer";
import { useIndParent } from "../../hooks/useIndParent";
import { createColumns } from "../../utils/functions";
import { AddOrgModal } from "../AddOrgModal";
import { AlertDialog } from "../AlertDialog";

export const Students = ({ user, userRole, isTeacher }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { students, isInviting, createdChilds, indexRef } = state;
  const { childs, updateChilds } = useIndParent(user, isTeacher);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: createdChildsIsOpen,
    onOpen: createdChildsOnOpen,
    onClose: createdChildsOnClose,
  } = useDisclosure();
  const {
    isOpen: addOrgModalIsOpen,
    onOpen: addOrgModalOnOpen,
    onClose: addOrgModalOnClose,
  } = useDisclosure();
  const {
    isOpen: alertIsOpen,
    onOpen: alertOnOpen,
    onClose: alertOnClose,
  } = useDisclosure();
  const toast = useToast();

  const showToast = (title, status) => {
    toast({
      position: "top",
      title,
      status,
      duration: 9000,
      isClosable: true,
    });
  };

  const [createChilds] = useMutation(CREATE_CHILD, {
    onCompleted: (data) => {
      const students = data.inviteDynoPlusStudent;
      if (students.length > 0) {
        dispatch({ type: "SET_CREATED_CHILDS", payload: students });
        updateChilds([
          ...childs,
          ...students.map((st) => ({
            firstName: st.firstName,
            lastName: st.lastName,
            username: st.username,
            isResetting: false,
          })),
        ]);
        showToast(
          `Successfully create ${
            userRole === "Teacher" ? "students" : "childs"
          } and sent their credentials to your mail.`,
          "success"
        );
        onModalClose();
        createdChildsOnOpen();
      } else {
        onModalClose();
        showToast(
          `Failed to create ${
            userRole === "Teacher" ? "students" : "childs"
          }. Please try again.`,
          "error"
        );
      }
    },
    onError: () => {
      onModalClose();
      showToast(
        `Failed to create ${
          userRole === "Teacher" ? "students" : "childs"
        }. Please try again.`,
        "error"
      );
    },
  });

  const [resetChildPassword] = useMutation(RESET_PASSWORD, {
    onCompleted: (data) => {
      const msg = data.resetPasswordDynosPlusStudent;
      const childsCopy = [...childs];
      childsCopy[indexRef]["isResetting"] = false;
      updateChilds(childsCopy);
      showToast(msg, "success");
    },
    onError: () => {
      showToast("Failed to reset password. Please try again.", "error");
    },
  });

  const resetPassword = (child) => {
    const childsCopy = [...childs];
    const index = childsCopy.findIndex((c) => c.id === child.id);
    dispatch({ type: "SET_INDEX_REF", payload: index });
    childsCopy[index]["isResetting"] = true;
    updateChilds(childsCopy);
    resetChildPassword({
      variables: {
        where: {
          id: child.id,
          email: user.email,
        },
      },
    });
  };

  const onNameChange = (e, id) => {
    let stduentsCopy = [...students];
    let index = stduentsCopy.findIndex((s) => s.id === id);
    if (index > -1) {
      stduentsCopy[index][e.target.name] = e.target.value;
      dispatch({ type: "SET_STUDENTS", payload: stduentsCopy });
    }
  };

  const onModalClose = () => {
    dispatch({
      type: "UPDATE_STATE",
      payload: {
        students: [{ id: "01", firstName: "", lastName: "" }],
        isInviting: false,
      },
    });
    onClose();
  };

  const addNewStudent = () => {
    const length = students.length;
    dispatch({
      type: "SET_STUDENTS",
      payload: [
        ...students,
        { id: `0${length + 1}`, firstName: "", lastName: "" },
      ],
    });
  };

  const removeChild = (id) => {
    dispatch({
      type: "SET_STUDENTS",
      payload: students.filter((s) => s.id !== id),
    });
  };

  const inviteStudents = (students) => {
    dispatch({ type: "SET_IS_INVITING", payload: true });
    createChilds({
      variables: {
        data: {
          email: user.email,
          studentsList: students.map((st) => ({
            firstName: st.firstName,
            lastName: st.lastName,
          })),
        },
      },
    });
  };

  const studentColumns = createColumns([
    { name: "First Name", accessor: "firstName" },
    { name: "Last Name", accessor: "lastName" },
    { name: "Username", accessor: "username" },
    {
      name: "Reset Password",
      accessor: "isResetting",
      Cell: ({ cell }) => {
        return (
          <Button
            colorScheme="brand"
            aria-label="reset password"
            isLoading={cell.value}
            loadingText="Resetting"
            onClick={() => resetPassword(cell.row.original)}
          >
            Reset
          </Button>
        );
      },
    },
  ]);

  const createdChildsColumns = createColumns([
    { name: "First Name", accessor: "firstName" },
    { name: "Last Name", accessor: "lastName" },
    { name: "Username", accessor: "username" },
    { name: "Password", accessor: "pwd" },
  ]);

  return (
    <Box
      style={{
        maxWidth: "1180px",
        margin: "0 auto",
        padding: "30px 20px",
      }}
    >
      <Flex justifyContent="flex-end">
        <Button
          colorScheme="brand"
          onClick={() => {
            if ((isTeacher && user?.organizations?.length > 0) || !isTeacher) {
              onOpen();
            } else {
              alertOnOpen();
            }
          }}
        >
          {isTeacher ? "Add Students" : "Add Childs"}
        </Button>
      </Flex>
      <DataTable
        columns={studentColumns}
        data={childs}
        showNumberOfRecords={100}
      />
      <StudentsInviteBox
        isTeacher={isTeacher}
        students={students}
        isOpen={isOpen}
        onClose={onClose}
        inviteStudents={inviteStudents}
        isInviting={isInviting}
        onNameChange={onNameChange}
        addNewStudent={addNewStudent}
        removeChild={removeChild}
        onModalClose={onModalClose}
      />
      <ModalBox
        title={isTeacher ? "Created students info" : "Created childs info"}
        isOpen={createdChildsIsOpen}
        onClose={createdChildsOnClose}
        modalProps={{
          closeOnOverlayClick: false,
          scrollBehavior: "inside",
        }}
        size="600px"
      >
        <ModalBody>
          <DataTable
            columns={createdChildsColumns}
            data={createdChilds}
            showNumberOfRecords={100}
          />
        </ModalBody>
      </ModalBox>
      <AlertDialog
        title="Info"
        description="You haven't added any organization yet. Please add to create classes and adding students."
        actionButtonText="Add school or organization"
        isDeleteButton={false}
        isOpen={alertIsOpen}
        onCancel={alertOnClose}
        onDelete={() => {
          alertOnClose();
          addOrgModalOnOpen();
        }}
        isWorking={false}
        loadingText=""
      />
      <AddOrgModal
        user={user}
        isTeacher={userRole === "Teacher"}
        isOpen={addOrgModalIsOpen}
        onClose={addOrgModalOnClose}
      />
    </Box>
  );
};
